import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Modal, Spin, Alert } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { stripeCurrency } from "./constants";
import CheckoutForm from "./CheckoutForm";
import { axiosInstant } from "../../api";
import { showError } from "../../Notifications";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || "");

interface Props {
  open: boolean;
  amount: number;
  planKey: string;
  onCancel: () => void;
}
const PaymentForm: FC<Props> = ({ open, onCancel, amount, planKey }) => {
  const submitBtnRef = useRef<HTMLFormElement>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [intentError, setIntentError] = useState("");

  const submitForm = () => submitBtnRef.current?.click();

  useEffect(() => {
    if (amount > 0 && open) {
      setLoadingIntent(true);
      setIntentError("");
      axiosInstant
        .post("/subscription/paymentIntent", {
          currency: stripeCurrency,
          amount: amount * 100,
        })
        .then((res) => {
          const secret = res?.data?.client_secret;
          if (secret) setClientSecret(secret);
          else setIntentError("Could not initialise payment. Please try again.");
        })
        .catch((error) => {
          const msg =
            error.response?.status === 403
              ? "Only recruiter accounts can make payments. Please log in as a recruiter."
              : error.response?.status === 401
              ? "Please log in to your recruiter account before purchasing."
              : "Could not connect to payment service. Please try again.";
          setIntentError(msg);
        })
        .finally(() => setLoadingIntent(false));
    }
  }, [amount, open]);

  useEffect(() => {
    if (!open) {
      setClientSecret("");
      setIntentError("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={submitForm}
      okText="Pay Now"
      okButtonProps={{ disabled: !clientSecret || loadingIntent }}
      title={`Pay $${amount} to Subscribe`}
    >
      {loadingIntent && (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 12, color: "#666" }}>Setting up secure payment...</p>
        </div>
      )}
      {!loadingIntent && intentError && (
        <Alert type="error" message={intentError} showIcon style={{ marginBottom: 16 }} />
      )}
      {!loadingIntent && !intentError && clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm submitBtnRef={submitBtnRef} planKey={planKey} amount={amount} />
        </Elements>
      )}
    </Modal>
  );
};

export default PaymentForm;
